import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useSEOMeta } from "@/components/SEO";

const PRESET_AMOUNTS = [10, 25, 50, 100, 250, 500];

const IMPACT_ITEMS = [
  {
    amount: "$10",
    icon: "ri-drop-line",
    title: "Clean Water Access",
    desc: "Provides clean water hygiene kits for one family for a month",
    color: "bg-teal-50 border-teal-200",
    iconColor: "text-teal-600 bg-teal-100",
  },
  {
    amount: "$25",
    icon: "ri-heart-pulse-line",
    title: "Health Screening",
    desc: "Funds a full health screening session for 5 community members",
    color: "bg-rose-50 border-rose-200",
    iconColor: "text-rose-600 bg-rose-100",
  },
  {
    amount: "$50",
    icon: "ri-book-open-line",
    title: "School Supplies",
    desc: "Equips 10 children with essential school materials for a term",
    color: "bg-amber-50 border-amber-200",
    iconColor: "text-amber-600 bg-amber-100",
  },
  {
    amount: "$100",
    icon: "ri-seedling-line",
    title: "Livelihood Training",
    desc: "Sponsors one woman through a full economic empowerment workshop",
    color: "bg-emerald-50 border-emerald-200",
    iconColor: "text-emerald-600 bg-emerald-100",
  },
  {
    amount: "$250",
    icon: "ri-building-line",
    title: "School Renovation",
    desc: "Contributes to rebuilding a classroom for 30+ students",
    color: "bg-stone-50 border-stone-200",
    iconColor: "text-stone-600 bg-stone-100",
  },
  {
    amount: "$500",
    icon: "ri-community-line",
    title: "Community Campaign",
    desc: "Funds a full WASH sensitization campaign in a rural community",
    color: "bg-primary/5 border-primary/20",
    iconColor: "text-primary bg-primary/10",
  },
];

const DONATION_METHODS = [
  {
    icon: "ri-bank-card-line",
    title: "Mobile Money",
    desc: "MTN MoMo / Orange Money",
    detail: "+237 670 396 340",
    badge: "Cameroon",
  },
  {
    icon: "ri-bank-line",
    title: "Bank Transfer",
    desc: "Afriland First Bank",
    detail: "Account: 10005-00001-09876543210-76",
    badge: "International",
  },
  {
    icon: "ri-mail-send-line",
    title: "Email Us",
    desc: "For wire transfers & large donations",
    detail: "info@bhodevcameroon.org",
    badge: "All Methods",
  },
];

export default function DonatePage() {
  useSEOMeta({
    title: "Donate to BHODEV Cameroon | Support Health & Development in Cameroon",
    description:
      "Support BHODEV's mission to promote health, education, and sustainable development in Cameroon. Your donation funds clean water, health screenings, school supplies, and community empowerment programs.",
    keywords: "donate BHODEV, support Cameroon NGO, humanitarian donation, health development Cameroon, BHODEV fundraising",
    canonicalUrl: "https://bhodevcameroon.org/donate",
  });

  const [selectedAmount, setSelectedAmount] = useState<number | null>(50);
  const [customAmount, setCustomAmount] = useState("");
  const [donationType, setDonationType] = useState<"once" | "monthly">("once");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    anonymous: false,
  });

  const finalAmount = customAmount ? parseFloat(customAmount) : selectedAmount;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!finalAmount || finalAmount <= 0) return;
    setSubmitting(true);

    const formData = new URLSearchParams();
    formData.append("first_name", form.firstName);
    formData.append("last_name", form.lastName);
    formData.append("email", form.email);
    formData.append("phone", form.phone);
    formData.append("amount", String(finalAmount));
    formData.append("donation_type", donationType);
    formData.append("message", form.message);
    formData.append("anonymous", form.anonymous ? "Yes" : "No");

    try {
      await fetch("https://readdy.ai/api/form/d824vod979rtuqs72grg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
    } catch (_) {
      // silent
    }

    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <main className="bg-white font-inter">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative h-[540px] md:h-[640px] flex items-center justify-center overflow-hidden">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fdistribution-of-water-hygiene-and-sanitation-kits-to-victims-of-flood-incident-in-limbe%2FIMG-20231011-WA0007.jpg?alt=media&token=4b9c57c0-1a32-41ae-ae71-80efc05a4e74"
          alt="BHODEV community health workers distributing aid in Cameroon"
          title="BHODEV community health workers distributing aid in Cameroon"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-black/75"></div>
        <div className="relative z-10 text-center text-white px-4 w-full max-w-4xl mx-auto">
          <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-6 tracking-widest uppercase">
            <i className="ri-heart-fill text-primary"></i>
            Make a Difference Today
          </span>
          <h1 className="text-4xl md:text-6xl font-bold mb-5 leading-tight tracking-tight">
            Your Gift Changes <span className="text-primary">Lives</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Every donation to BHODEV directly funds health programs, clean water access, education, and economic empowerment for vulnerable communities in Cameroon.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <a
              href="#donate-form"
              className="bg-primary text-white px-8 py-3.5 rounded-full font-bold text-sm hover:bg-primary-dark transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Donate Now
            </a>
            <a
              href="#impact"
              className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-8 py-3.5 rounded-full font-bold text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              See Your Impact
            </a>
          </div>
        </div>
      </section>

      {/* ── TRUST STATS ── */}
      <section className="bg-stone-900 py-10 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: "50K+", label: "People Impacted", icon: "ri-group-line" },
            { value: "10+", label: "Years of Service", icon: "ri-calendar-check-line" },
            { value: "100%", label: "Transparent Funding", icon: "ri-shield-check-line" },
            { value: "8+", label: "SDGs Addressed", icon: "ri-global-line" },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/20 mb-1">
                <i className={`${stat.icon} text-primary text-lg`}></i>
              </div>
              <p className="text-3xl font-bold text-white">{stat.value}</p>
              <p className="text-stone-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── YOUR IMPACT ── */}
      <section id="impact" className="py-24 px-4 bg-stone-50 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block bg-secondary text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-4">
              What Your Gift Does
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight">
              See Your <span className="text-primary">Impact</span>
            </h2>
            <p className="text-stone-500 mt-4 max-w-xl mx-auto text-base">
              Every dollar you give is put to work in the communities that need it most.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {IMPACT_ITEMS.map((item) => (
              <div
                key={item.amount}
                className={`${item.color} border rounded-2xl p-6 flex flex-col gap-4 hover:-translate-y-1 transition-transform duration-300`}
              >
                <div className="flex items-center justify-between">
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${item.iconColor}`}>
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <span className="text-2xl font-black text-stone-800">{item.amount}</span>
                </div>
                <div>
                  <h3 className="font-bold text-stone-900 text-base">{item.title}</h3>
                  <p className="text-stone-500 text-sm mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DONATION FORM ── */}
      <section id="donate-form" className="py-24 px-4 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

            {/* Left: form */}
            <div className="lg:col-span-3">
              <span className="inline-block bg-secondary text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-4">
                Make a Donation
              </span>
              <h2 className="text-4xl font-bold text-stone-900 mb-2 leading-tight">
                Give to <span className="text-primary">BHODEV</span>
              </h2>
              <p className="text-stone-500 text-base mb-8">
                Fill in the form below and our team will follow up with payment instructions.
              </p>

              {submitted ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-5">
                    <i className="ri-checkbox-circle-line text-emerald-600 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">Thank You for Your Generosity!</h3>
                  <p className="text-stone-500 text-sm leading-relaxed max-w-sm mx-auto mb-6">
                    We've received your donation intent of <strong className="text-stone-800">${finalAmount}</strong>. Our team will reach out to you at <strong className="text-stone-800">{form.email}</strong> with payment instructions within 24 hours.
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <button
                      onClick={() => { setSubmitted(false); setForm({ firstName: "", lastName: "", email: "", phone: "", message: "", anonymous: false }); setSelectedAmount(50); setCustomAmount(""); }}
                      className="bg-primary text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-dark transition-colors duration-300 cursor-pointer whitespace-nowrap"
                    >
                      Make Another Donation
                    </button>
                    <Link
                      to="/"
                      className="bg-stone-100 text-stone-700 px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-stone-200 transition-colors duration-300 whitespace-nowrap"
                    >
                      Back to Home
                    </Link>
                  </div>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="donation-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-6"
                >
                  {/* Donation type toggle */}
                  <div>
                    <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-2">
                      Donation Type
                    </label>
                    <div className="inline-flex bg-stone-100 rounded-full p-1">
                      {(["once", "monthly"] as const).map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setDonationType(type)}
                          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                            donationType === type
                              ? "bg-white text-stone-900 shadow-sm"
                              : "text-stone-500 hover:text-stone-700"
                          }`}
                        >
                          {type === "once" ? "One-time" : "Monthly"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Amount selector */}
                  <div>
                    <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-3">
                      Select Amount (USD)
                    </label>
                    <div className="grid grid-cols-3 gap-2.5 mb-3">
                      {PRESET_AMOUNTS.map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => { setSelectedAmount(amt); setCustomAmount(""); }}
                          className={`py-3 rounded-xl text-sm font-bold border-2 transition-all duration-200 cursor-pointer whitespace-nowrap ${
                            selectedAmount === amt && !customAmount
                              ? "bg-primary text-white border-primary"
                              : "bg-white text-stone-700 border-stone-200 hover:border-primary hover:text-primary"
                          }`}
                        >
                          ${amt}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 font-semibold text-sm">$</span>
                      <input
                        type="number"
                        min="1"
                        placeholder="Custom amount"
                        value={customAmount}
                        onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                        className="w-full pl-8 pr-4 py-3 rounded-xl border-2 border-stone-200 focus:outline-none focus:border-primary text-sm text-stone-800 placeholder-stone-400 transition-colors duration-200"
                      />
                    </div>
                  </div>

                  {/* Personal info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        value={form.firstName}
                        onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                        placeholder="John"
                        className="w-full px-4 py-3 rounded-xl border-2 border-stone-200 focus:outline-none focus:border-primary text-sm text-stone-800 placeholder-stone-400 transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        value={form.lastName}
                        onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                        placeholder="Doe"
                        className="w-full px-4 py-3 rounded-xl border-2 border-stone-200 focus:outline-none focus:border-primary text-sm text-stone-800 placeholder-stone-400 transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 rounded-xl border-2 border-stone-200 focus:outline-none focus:border-primary text-sm text-stone-800 placeholder-stone-400 transition-colors duration-200"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Phone (Optional)</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="+237 6XX XXX XXX"
                        className="w-full px-4 py-3 rounded-xl border-2 border-stone-200 focus:outline-none focus:border-primary text-sm text-stone-800 placeholder-stone-400 transition-colors duration-200"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Message (Optional)</label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Share why you're donating or any special instructions..."
                      className="w-full px-4 py-3 rounded-xl border-2 border-stone-200 focus:outline-none focus:border-primary text-sm text-stone-800 placeholder-stone-400 transition-colors duration-200 resize-none"
                    />
                    <p className="text-xs text-stone-400 text-right">{form.message.length}/500</p>
                  </div>

                  {/* Anonymous toggle */}
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div
                      onClick={() => setForm({ ...form, anonymous: !form.anonymous })}
                      className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer shrink-0 ${form.anonymous ? "bg-primary" : "bg-stone-300"}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all duration-300 ${form.anonymous ? "left-5" : "left-0.5"}`}></span>
                    </div>
                    <span className="text-sm text-stone-600 group-hover:text-stone-800 transition-colors duration-200">
                      Make this donation anonymous
                    </span>
                  </label>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting || !finalAmount || (finalAmount ?? 0) <= 0}
                    className="w-full bg-primary text-white py-4 rounded-xl font-bold text-base hover:bg-primary-dark disabled:opacity-60 transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="ri-heart-fill"></i>
                        Donate {finalAmount ? `$${finalAmount}` : ""} {donationType === "monthly" ? "/ month" : ""}
                      </>
                    )}
                  </button>

                  <p className="text-xs text-stone-400 text-center leading-relaxed">
                    By submitting, our team will contact you with secure payment instructions. Your information is kept confidential.
                  </p>
                </form>
              )}
            </div>

            {/* Right: sidebar info */}
            <div className="lg:col-span-2 flex flex-col gap-5 lg:sticky lg:top-28">
              {/* Payment methods */}
              <div className="bg-stone-50 rounded-2xl border border-stone-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-stone-200 bg-white">
                  <h3 className="text-sm font-bold text-stone-800">Payment Methods</h3>
                  <p className="text-xs text-stone-400 mt-0.5">We'll send you instructions after submission</p>
                </div>
                <div className="divide-y divide-stone-200">
                  {DONATION_METHODS.map((method) => (
                    <div key={method.title} className="flex items-start gap-3 px-5 py-4">
                      <div className="w-9 h-9 flex items-center justify-center bg-primary/10 rounded-lg shrink-0 mt-0.5">
                        <i className={`${method.icon} text-primary text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-stone-800">{method.title}</p>
                          <span className="text-xs bg-secondary text-primary px-2 py-0.5 rounded-full font-medium">{method.badge}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trust badges */}
              <div className="bg-white rounded-2xl border border-stone-200 p-5 flex flex-col gap-4">
                <h3 className="text-sm font-bold text-stone-800">Why Donate to BHODEV?</h3>
                {[
                  { icon: "ri-shield-check-line", text: "Registered NGO — legalized in Cameroon since 2019" },
                  { icon: "ri-file-list-3-line", text: "Full financial transparency & annual reporting" },
                  { icon: "ri-map-pin-line", text: "100% of funds go directly to field programs" },
                  { icon: "ri-global-line", text: "Aligned with 8+ UN Sustainable Development Goals" },
                ].map((item) => (
                  <div key={item.text} className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-emerald-100 rounded-lg shrink-0 mt-0.5">
                      <i className={`${item.icon} text-emerald-600 text-sm`}></i>
                    </div>
                    <p className="text-xs text-stone-600 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>

              {/* Contact for large donations */}
              <div className="bg-primary rounded-2xl p-5 text-white">
                <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-lg mb-3">
                  <i className="ri-customer-service-2-line text-white text-base"></i>
                </div>
                <h3 className="text-sm font-bold mb-1">Large Donations & Partnerships</h3>
                <p className="text-xs text-white/75 leading-relaxed mb-4">
                  For donations above $1,000 or institutional partnerships, contact us directly for a tailored giving plan.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-white text-primary px-4 py-2 rounded-full text-xs font-bold hover:bg-stone-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line"></i>
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL / STORY ── */}
      <section className="py-20 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-secondary text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-4">
              Stories of Change
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900">
              Your Donation at <span className="text-primary">Work</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                img: "https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frehabilitation-of-water-catchment-in-wonya-mavio%2FIMG-20230706-WA0017.jpg?alt=media&token=2040f2e6-9187-44d9-b0c0-eb72071c96b3",
                title: "Clean Water in Wonya Mavio",
                desc: "Rehabilitation of the water catchment restored clean water access to over 500 families in the Wonya Mavio community.",
                tag: "Water & Sanitation",
              },
              {
                img: "https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frenovation-and-reconstruction-of-bomaka-primary-school-buildings%2FIMG-20231209-WA0097.jpg?alt=media&token=31861194-44f4-40b4-a716-64d5349acdc6",
                title: "Bomaka School Rebuilt",
                desc: "Renovation of Bomaka Primary School gave hundreds of children a safe, dignified learning environment.",
                tag: "Education",
              },
              {
                img: "https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fsensitization-on-water-hygiene-and-sanitation-mile-16%2FIMG-20230504-WA0066.jpg?alt=media&token=c1894e5b-f286-423f-ac50-4c54636d8865",
                title: "WASH Sensitization Mile 16",
                desc: "Community-wide hygiene and sanitation campaigns reached thousands of residents in the Mile 16 area.",
                tag: "Health",
              },
            ].map((story) => (
              <div key={story.title} className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:-translate-y-1 transition-transform duration-300">
                <div className="w-full h-52 overflow-hidden">
                  <img
                    src={story.img}
                    alt={story.title}
                    title={story.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5">
                  <span className="text-xs font-semibold text-primary bg-secondary px-2.5 py-1 rounded-full">{story.tag}</span>
                  <h3 className="font-bold text-stone-900 text-base mt-3 mb-2">{story.title}</h3>
                  <p className="text-stone-500 text-sm leading-relaxed">{story.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/our-work"
              className="inline-flex items-center gap-2 bg-white border-2 border-stone-200 text-stone-700 px-6 py-3 rounded-full text-sm font-semibold hover:border-primary hover:text-primary transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-right-line"></i>
              See All Our Work
            </Link>
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ── */}
      <section className="py-20 px-4 bg-primary">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
            <i className="ri-heart-fill text-white text-2xl"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Every Gift Matters</h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            Whether it's $10 or $10,000 — your generosity fuels BHODEV's mission to build healthier, more resilient communities across Cameroon.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="#donate-form"
              className="bg-white text-primary px-8 py-3.5 rounded-full font-bold text-sm hover:bg-stone-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Donate Now
            </a>
            <Link
              to="/get-involved"
              className="bg-white/10 border border-white/30 text-white px-8 py-3.5 rounded-full font-bold text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Other Ways to Help
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}